/* eslint-disable max-lines */
import { deepPurple } from "@mui/material/colors";
import { type PaletteOptions } from "@mui/material/styles";

const COLOR_NAVBAR_AVATAR_BACKGROUND = "#F4F2EC";
const COLOR_NAVBAR_AVATAR_TEXT = "#7B645C";
const COLOR_NAVBAR_BACKGROUND = "#F4F2EC";
const COLOR_NAVBAR_ITEM_CONTENT = "#7B645C";
const COLOR_NAVBAR_ITEM_CONTENT_SELECTED = "#210E10";
const COLOR_NAVBAR_ITEM_SELECTED_BACKGROUND = "#ECE7DC";
const COLOR_NAVBAR_SECTION_TITLE = "#9F8D7F";
const COLOR_NAVBAR_SECTION_DIVIDER = "#DDD8C9";
const COLOR_NAVBAR_USERNAME = "#210E10";

const COLOR_FLOATING_NAVIGATION_BACKGROUND = "#37171A";

const COLOR_FLOATING_NAVIGATION_SELECTED_BACKGROUND = "#9A9A9A69"; // 40% opacity

// Shift card gradient border
const COLOR_SHIFT_CARD_BORDER = "#DDD8C9";

// Berlin color tokens
const COLOR_STRONG = "#210E10";
const COLOR_BASE = "#37171A";

// Avatar initials fallback background
const COLOR_AVATAR_INITIALS_BACKGROUND = COLOR_BASE;
const COLOR_PRIMARY = "#DA2451";
const COLOR_PRIMARY_DARK = "#862335";
const COLOR_SECONDARY = "#37171A";
const COLOR_MUTED = "#7B645C";
const COLOR_DANGER = "#DD3630";
const COLOR_INACTIVE = "#9F8D7F";
export const COLOR_INACTIVE_SOLID = "#9F8D7F"; // solid equivalent of COLOR_INACTIVE on white background, useful when opacity is not desired (e.g., strikethrough)

// Berlin inverted color tokens
const COLOR_INVERTED_STRONG = "#FEFCF8";
const COLOR_INVERTED_BASE = "#FEFCF8";
const COLOR_INVERTED_MUTED = "#F9F8F5";
const COLOR_INVERTED_INACTIVE = "#BFB3A4";

// Berlin background-color tokens
const COLOR_BACKGROUND_COLOR_BASE = "#FEFCF8";
const COLOR_BACKGROUND_COLOR_BASE_DARK = "#F9F8F5";
const COLOR_BACKGROUND_COLOR_MUTED = "#F4F2EC";
const COLOR_BACKGROUND_COLOR_INACTIVE = "#ECE7DC";
const COLOR_BACKGROUND_COLOR_DIM = "#DDD8C9";
const COLOR_BACKGROUND_COLOR_ALPHA = "#F9F8F5";
const COLOR_BACKGROUND_COLOR_STANDOUT = "#FFFFFF";
const COLOR_BACKGROUND_COLOR_PRIMARY = "#FDE9EA";
// Time slot palette tokens (saturated / pastel / accent)
const COLOR_TIME_SLOT_AM_MAIN = "#FDCCA6";
const COLOR_TIME_SLOT_AM_LIGHT = "#FFE9D7";
const COLOR_TIME_SLOT_AM_DARK = "#F69551";
const COLOR_TIME_SLOT_AM_DEEP = "#CA7236";
const COLOR_TIME_SLOT_PM_MAIN = "#A3D8FA";
const COLOR_TIME_SLOT_PM_LIGHT = "#D0EEFF";
const COLOR_TIME_SLOT_PM_DARK = "#4DBEF5";
const COLOR_TIME_SLOT_PM_DEEP = "#2E96C6";
const COLOR_TIME_SLOT_NOC_MAIN = "#CCB8FF";
const COLOR_TIME_SLOT_NOC_LIGHT = "#E1D4FF";
const COLOR_TIME_SLOT_NOC_DARK = "#A072F7";
const COLOR_TIME_SLOT_NOC_DEEP = "#6A2CC3";
const COLOR_TIME_SLOT_ORI_MAIN = "#A6E5BD";
const COLOR_TIME_SLOT_ORI_LIGHT = "#D2F1DE";
const COLOR_TIME_SLOT_ORI_DARK = "#5EB174";
const COLOR_BACKGROUND_COLOR_WARNING = "#FBFCE5";
const COLOR_BACKGROUND_COLOR_ERROR = "#FFD9D2";
const COLOR_BACKGROUND_COLOR_PRIMARY_SOLID = "#FDE9EA";
// Berlin chat message background colors
const COLOR_CHAT_MESSAGE_INCOMING = "#F4F2EC"; // 5% opacity - light gray for incoming messages
const COLOR_CHAT_MESSAGE_OUTGOING = COLOR_BASE; // Dark brown for outgoing messages

// Berlin background-color inverted tokens
const COLOR_BACKGROUND_COLOR_INVERTED_MUTED = "#FFFFFF0D"; // 5% opacity

// Berlin border-color tokens
const COLOR_BORDER_COLOR_BASE = "#ECE7DC";
const COLOR_BORDER_COLOR_MUTED = "#F4F2EC";
const COLOR_BORDER_COLOR_STRONG = "#DDD8C9";
const COLOR_BORDER_COLOR_INTENSE = "#BFB3A4";
const COLOR_BORDER_COLOR_PRIMARY = "#F9A5AD";
export const COLOR_BORDER_COLOR_WARNING = "#FAFA64";
const COLOR_BORDER_COLOR_ERROR = "#FF9A87";

// Berlin links tokens
const COLOR_LINKS = "#37171A";

// Berlin button style tokens
const COLOR_BUTTON_TRANSPARENT = "#FFFFFF00"; // 0% opacity
const COLOR_BUTTON_PRIMARY_HOVER = "#862335";
const COLOR_BUTTON_SECONDARY = "#FEFCF8";
const COLOR_BUTTON_SECONDARY_HOVER = "#F4F2EC";
const COLOR_BUTTON_DISABLED_COLOR = "#BFB3A4";
const COLOR_BUTTON_DISABLED_BACKGROUND = "#ECE7DC";
const COLOR_BUTTON_LINK_HOVER = "#5D403C";
const COLOR_BUTTON_LINK_HOVER_BACKGROUND = "#FFFFFF00";
const COLOR_BUTTON_LINK_DISABLED = "#BFB3A4";
const COLOR_BUTTON_LINK_INVERTED = "#FFFFFFCC"; // 80% opacity
const COLOR_BUTTON_LINK_INVERTED_HOVER_BACKGROUND = "#FFFFFF1A"; // 10% opacity
const COLOR_BUTTON_LINK_INVERTED_HOVER_BORDER = "#FFFFFF1A"; // 10% opacity
const COLOR_BUTTON_LINK_INVERTED_DISABLED = "#FFFFFF33"; // 20% opacity
const COLOR_BUTTON_MUTED_COLOR = "#37171A";
const COLOR_BUTTON_MUTED_HOVER_COLOR = "#210E10";
const COLOR_BUTTON_MUTED_HOVER_BACKGROUND = "#F4F2EC";
const COLOR_BUTTON_MUTED_HOVER_BORDER = "#FFFFFF00";
const COLOR_BUTTON_OUTLINE_BORDER = "#ECE7DC";
const COLOR_BUTTON_OUTLINE_DISABLED = "#BFB3A4";
const COLOR_BUTTON_OUTLINE_DISABLED_BORDER = "#FFFFFF00";
const COLOR_BUTTON_OUTLINE_INVERTED = "#FFFFFFCC"; // 80% opacity
const COLOR_BUTTON_OUTLINE_INVERTED_BORDER = "#FFFFFF1A"; // 10% opacity
const COLOR_BUTTON_OUTLINE_INVERTED_HOVER_BACKGROUND = "#FFFFFF1A"; // 10% opacity
const COLOR_BUTTON_OUTLINE_INVERTED_HOVER_BORDER = "#FFFFFF33"; // 20% opacity
const COLOR_BUTTON_OUTLINE_INVERTED_DISABLED = "#FFFFFF33"; // 20% opacity
const COLOR_BUTTON_OUTLINE_INVERTED_DISABLED_BACKGROUND = "#FFFFFF0D"; // 5% opacity
const COLOR_BUTTON_OUTLINE_INVERTED_DISABLED_BORDER = "#FFFFFF0D"; // 5% opacity
const COLOR_BUTTON_WARNING = "#CE7210";
const COLOR_BUTTON_WARNING_HOVER = "#D8862E";
const COLOR_BUTTON_DANGER_FILLED = COLOR_DANGER;
const COLOR_BUTTON_DANGER_FILLED_HOVER = "#B31414";

// Berlin shifts/progresses tokens
const COLOR_PROGRESS_POSITIVE = "#24C23F";
const COLOR_PROGRESS_PENDING = "#249A34";
const COLOR_PROGRESS_NEGATIVE = "#F6524C";
const COLOR_PROGRESS_NEUTRAL = "#DDD8C9";

// Berlin status chip tokens
const COLOR_STATUS_WARNING = "#CE7210";
const COLOR_STATUS_PENDING = "#EF6C00"; // Ember orange
const COLOR_BACKGROUND_COLOR_SUCCESS = "#D2F2D2";
const COLOR_BACKGROUND_COLOR_PENDING = "#EF6C000F"; // 6% opacity of pending orange

// Status ring colors for shift tile worker avatars (from Figma node 11:2563)
const COLOR_STATUS_RING_POSITIVE = "#24C23F";
const COLOR_STATUS_RING_WARNING = "#E8E355";
const COLOR_STATUS_RING_NEGATIVE = "#F6524C";
const COLOR_STATUS_RING_NEUTRAL = "#37171A";

// MUI X Charts "Rainbow Surge" palette (light). Chart series cycle through these
// in order, wrapping back to the start when more series than colors are present.
export const CHART_SERIES_COLORS = [
  "#4254FB", // blue
  "#FFB422", // amber
  "#FA4F58", // red
  "#0DBEFF", // cyan
  "#22BF75", // green
  "#FA83B4", // pink
  "#FF7511", // orange
];

export interface ButtonStateColorMap {
  color: string;
  backgroundColor: string;
  borderColor: string;
}

export const muiThemePalette: PaletteOptions = {
  primary: { main: COLOR_PRIMARY },
  warning: { main: COLOR_STATUS_WARNING },
  info: { main: COLOR_PRIMARY },
  accent: {
    main: deepPurple[800],
    contrastText: "#FFFFFF",
  },
  am: {
    main: COLOR_TIME_SLOT_AM_MAIN,
    light: COLOR_TIME_SLOT_AM_LIGHT,
    dark: COLOR_TIME_SLOT_AM_DARK,
    deep: COLOR_TIME_SLOT_AM_DEEP,
  },
  pm: {
    main: COLOR_TIME_SLOT_PM_MAIN,
    light: COLOR_TIME_SLOT_PM_LIGHT,
    dark: COLOR_TIME_SLOT_PM_DARK,
    deep: COLOR_TIME_SLOT_PM_DEEP,
  },
  noc: {
    main: COLOR_TIME_SLOT_NOC_MAIN,
    light: COLOR_TIME_SLOT_NOC_LIGHT,
    dark: COLOR_TIME_SLOT_NOC_DARK,
    deep: COLOR_TIME_SLOT_NOC_DEEP,
  },
  ori: {
    main: COLOR_TIME_SLOT_ORI_MAIN,
    light: COLOR_TIME_SLOT_ORI_LIGHT,
    dark: COLOR_TIME_SLOT_ORI_DARK,
  },
  link: {
    base: COLOR_LINKS,
    inverted: COLOR_LINKS,
  },
  text: {
    invert: COLOR_INVERTED_BASE,
  },
  base: {
    strong: COLOR_STRONG,
    base: COLOR_BASE,
    primary: COLOR_PRIMARY,
    primaryDark: COLOR_PRIMARY_DARK,
    secondary: COLOR_SECONDARY,
    muted: COLOR_MUTED,
    danger: COLOR_DANGER,
    inactive: COLOR_INACTIVE,
    inverted: {
      strong: COLOR_INVERTED_STRONG,
      base: COLOR_INVERTED_BASE,
      muted: COLOR_INVERTED_MUTED,
      inactive: COLOR_INVERTED_INACTIVE,
    },
  },
  button: {
    primary: {
      normal: {
        color: COLOR_INVERTED_BASE,
        backgroundColor: COLOR_PRIMARY,
        borderColor: COLOR_BUTTON_TRANSPARENT,
      },
      hover: {
        color: COLOR_INVERTED_BASE,
        backgroundColor: COLOR_BUTTON_PRIMARY_HOVER,
        borderColor: COLOR_BUTTON_TRANSPARENT,
      },
      disabled: {
        color: COLOR_BUTTON_DISABLED_COLOR,
        backgroundColor: COLOR_BUTTON_DISABLED_BACKGROUND,
        borderColor: COLOR_BUTTON_TRANSPARENT,
      },
    },
    secondary: {
      normal: {
        color: COLOR_BASE,
        backgroundColor: COLOR_BUTTON_SECONDARY,
        borderColor: COLOR_BORDER_COLOR_BASE,
      },
      hover: {
        color: COLOR_BASE,
        backgroundColor: COLOR_BUTTON_SECONDARY_HOVER,
        borderColor: COLOR_BORDER_COLOR_STRONG,
      },
      disabled: {
        color: COLOR_BUTTON_DISABLED_COLOR,
        backgroundColor: COLOR_BUTTON_DISABLED_BACKGROUND,
        borderColor: COLOR_BUTTON_DISABLED_BACKGROUND,
      },
    },
    link: {
      normal: {
        color: COLOR_LINKS,
        backgroundColor: COLOR_BUTTON_TRANSPARENT,
        borderColor: COLOR_BUTTON_TRANSPARENT,
      },
      hover: {
        color: COLOR_BUTTON_LINK_HOVER,
        backgroundColor: COLOR_BUTTON_LINK_HOVER_BACKGROUND,
        borderColor: COLOR_BUTTON_TRANSPARENT,
      },
      disabled: {
        color: COLOR_BUTTON_LINK_DISABLED,
        backgroundColor: COLOR_BUTTON_TRANSPARENT,
        borderColor: COLOR_BUTTON_TRANSPARENT,
      },
      inverted: {
        normal: {
          color: COLOR_BUTTON_LINK_INVERTED,
          backgroundColor: COLOR_BUTTON_TRANSPARENT,
          borderColor: COLOR_BUTTON_TRANSPARENT,
        },
        hover: {
          color: COLOR_INVERTED_BASE,
          backgroundColor: COLOR_BUTTON_LINK_INVERTED_HOVER_BACKGROUND,
          borderColor: COLOR_BUTTON_LINK_INVERTED_HOVER_BORDER,
        },
        disabled: {
          color: COLOR_BUTTON_LINK_INVERTED_DISABLED,
          backgroundColor: COLOR_BUTTON_TRANSPARENT,
          borderColor: COLOR_BUTTON_TRANSPARENT,
        },
      },
    },
    muted: {
      normal: {
        color: COLOR_BUTTON_MUTED_COLOR,
        backgroundColor: COLOR_BUTTON_TRANSPARENT,
        borderColor: COLOR_BUTTON_TRANSPARENT,
      },
      hover: {
        color: COLOR_BUTTON_MUTED_HOVER_COLOR,
        backgroundColor: COLOR_BUTTON_MUTED_HOVER_BACKGROUND,
        borderColor: COLOR_BUTTON_MUTED_HOVER_BORDER,
      },
      disabled: {
        color: COLOR_BUTTON_DISABLED_COLOR,
        backgroundColor: COLOR_BUTTON_DISABLED_BACKGROUND,
        borderColor: COLOR_BUTTON_DISABLED_BACKGROUND,
      },
    },
    warning: {
      normal: {
        color: COLOR_INVERTED_BASE,
        backgroundColor: COLOR_BUTTON_WARNING,
        borderColor: COLOR_BUTTON_WARNING,
      },
      hover: {
        color: COLOR_INVERTED_BASE,
        backgroundColor: COLOR_BUTTON_WARNING_HOVER,
        borderColor: COLOR_BUTTON_WARNING_HOVER,
      },
      disabled: {
        color: COLOR_BUTTON_DISABLED_COLOR,
        backgroundColor: COLOR_BUTTON_DISABLED_BACKGROUND,
        borderColor: COLOR_BUTTON_DISABLED_BACKGROUND,
      },
    },
    dangerFilled: {
      normal: {
        color: COLOR_INVERTED_BASE,
        backgroundColor: COLOR_BUTTON_DANGER_FILLED,
        borderColor: COLOR_BUTTON_DANGER_FILLED,
      },
      hover: {
        color: COLOR_INVERTED_BASE,
        backgroundColor: COLOR_BUTTON_DANGER_FILLED_HOVER,
        borderColor: COLOR_BUTTON_DANGER_FILLED_HOVER,
      },
      disabled: {
        color: COLOR_BUTTON_DISABLED_COLOR,
        backgroundColor: COLOR_BUTTON_DISABLED_BACKGROUND,
        borderColor: COLOR_BUTTON_DISABLED_BACKGROUND,
      },
    },
    outline: {
      normal: {
        color: COLOR_BASE,
        backgroundColor: COLOR_BUTTON_SECONDARY,
        borderColor: COLOR_BUTTON_OUTLINE_BORDER,
      },
      hover: {
        color: COLOR_BASE,
        backgroundColor: COLOR_BUTTON_SECONDARY_HOVER,
        borderColor: COLOR_BORDER_COLOR_STRONG,
      },
      disabled: {
        color: COLOR_BUTTON_OUTLINE_DISABLED,
        backgroundColor: COLOR_BUTTON_DISABLED_BACKGROUND,
        borderColor: COLOR_BUTTON_OUTLINE_DISABLED_BORDER,
      },
      inverted: {
        normal: {
          color: COLOR_BUTTON_OUTLINE_INVERTED,
          backgroundColor: COLOR_BUTTON_TRANSPARENT,
          borderColor: COLOR_BUTTON_OUTLINE_INVERTED_BORDER,
        },
        hover: {
          color: COLOR_INVERTED_BASE,
          backgroundColor: COLOR_BUTTON_OUTLINE_INVERTED_HOVER_BACKGROUND,
          borderColor: COLOR_BUTTON_OUTLINE_INVERTED_HOVER_BORDER,
        },
        disabled: {
          color: COLOR_BUTTON_OUTLINE_INVERTED_DISABLED,
          backgroundColor: COLOR_BUTTON_OUTLINE_INVERTED_DISABLED_BACKGROUND,
          borderColor: COLOR_BUTTON_OUTLINE_INVERTED_DISABLED_BORDER,
        },
      },
    },
  },
  background: {
    default: COLOR_BACKGROUND_COLOR_BASE,
    paper: COLOR_BACKGROUND_COLOR_STANDOUT,
    base: COLOR_BACKGROUND_COLOR_BASE,
    baseDark: COLOR_BACKGROUND_COLOR_BASE_DARK,
    baseDarker: COLOR_BACKGROUND_COLOR_MUTED,
    medium: COLOR_BACKGROUND_COLOR_INACTIVE,
    muted: COLOR_BACKGROUND_COLOR_MUTED,
    inactive: COLOR_BACKGROUND_COLOR_INACTIVE,
    dim: COLOR_BACKGROUND_COLOR_DIM,
    alpha: COLOR_BACKGROUND_COLOR_ALPHA,
    warning: COLOR_BACKGROUND_COLOR_WARNING,
    error: COLOR_BACKGROUND_COLOR_ERROR,
    standout: COLOR_BACKGROUND_COLOR_STANDOUT,
    primary: COLOR_BACKGROUND_COLOR_PRIMARY,
    primarySolid: COLOR_BACKGROUND_COLOR_PRIMARY_SOLID,
    page: COLOR_BACKGROUND_COLOR_STANDOUT,
    pageShadow: "0px 1px 5px -1px rgba(0, 0, 0, 0.2)",
    surface: COLOR_BACKGROUND_COLOR_BASE,
    sectionHeader: "transparent",
    chat: {
      incoming: COLOR_CHAT_MESSAGE_INCOMING,
      outgoing: COLOR_CHAT_MESSAGE_OUTGOING,
    },
    inverted: {
      muted: COLOR_BACKGROUND_COLOR_INVERTED_MUTED,
    },
  },
  border: {
    base: COLOR_BORDER_COLOR_BASE,
    muted: COLOR_BORDER_COLOR_MUTED,
    strong: COLOR_BORDER_COLOR_STRONG,
    intense: COLOR_BORDER_COLOR_INTENSE,
    inactive: COLOR_BACKGROUND_COLOR_INACTIVE,
    primary: COLOR_BORDER_COLOR_PRIMARY,
    warning: COLOR_BORDER_COLOR_WARNING,
    error: COLOR_BORDER_COLOR_ERROR,
  },
  navbar: {
    avatarBackground: COLOR_NAVBAR_AVATAR_BACKGROUND,
    avatarText: COLOR_NAVBAR_AVATAR_TEXT,
    background: COLOR_NAVBAR_BACKGROUND,
    itemContent: COLOR_NAVBAR_ITEM_CONTENT,
    itemContentSelected: COLOR_NAVBAR_ITEM_CONTENT_SELECTED,
    itemSelectedBackground: COLOR_NAVBAR_ITEM_SELECTED_BACKGROUND,
    sectionTitle: COLOR_NAVBAR_SECTION_TITLE,
    sectionDivider: COLOR_NAVBAR_SECTION_DIVIDER,
    userName: COLOR_NAVBAR_USERNAME,
  },
  floatingNavigation: {
    background: COLOR_FLOATING_NAVIGATION_BACKGROUND,
    selectedBackground: COLOR_FLOATING_NAVIGATION_SELECTED_BACKGROUND,
  },
  shiftCard: {
    borderGradient: COLOR_SHIFT_CARD_BORDER,
  },
  avatar: {
    initialsBackground: COLOR_AVATAR_INITIALS_BACKGROUND,
  },
  icon: {
    default: COLOR_STRONG,
    muted: COLOR_MUTED,
    link: COLOR_LINKS,
    inverted: {
      default: COLOR_INVERTED_STRONG,
      muted: COLOR_INVERTED_MUTED,
      link: COLOR_LINKS,
    },
  },
  progress: {
    positive: COLOR_PROGRESS_POSITIVE,
    pending: COLOR_PROGRESS_PENDING,
    negative: COLOR_PROGRESS_NEGATIVE,
    neutral: COLOR_PROGRESS_NEUTRAL,
  },
  statusRing: {
    positive: COLOR_STATUS_RING_POSITIVE,
    warning: COLOR_STATUS_RING_WARNING,
    negative: COLOR_STATUS_RING_NEGATIVE,
    neutral: COLOR_STATUS_RING_NEUTRAL,
  },
  chart: {
    series: CHART_SERIES_COLORS,
  },
  statusChip: {
    success: {
      background: COLOR_BACKGROUND_COLOR_SUCCESS,
      text: COLOR_PROGRESS_POSITIVE,
    },
    error: {
      background: COLOR_BACKGROUND_COLOR_ERROR,
      text: COLOR_DANGER,
    },
    warning: {
      background: COLOR_BACKGROUND_COLOR_WARNING,
      text: COLOR_STATUS_WARNING,
    },
    info: {
      background: COLOR_BACKGROUND_COLOR_PRIMARY,
      text: COLOR_PRIMARY,
    },
    muted: {
      background: COLOR_BACKGROUND_COLOR_MUTED,
      text: COLOR_MUTED,
    },
    pending: {
      background: COLOR_BACKGROUND_COLOR_PENDING,
      text: COLOR_STATUS_PENDING,
    },
  },
  chip: {
    borderColor: "transparent",
    backgroundColor: COLOR_BACKGROUND_COLOR_INACTIVE,
  },
  subtleAction: {
    color: COLOR_BASE,
    hoverColor: COLOR_BASE,
    hoverBackground: COLOR_BACKGROUND_COLOR_BASE_DARK,
  },
  listItem: {
    selectedBackground: COLOR_BACKGROUND_COLOR_BASE_DARK,
    hoverBackground: COLOR_BACKGROUND_COLOR_BASE_DARK,
  },
};
/* eslint-enable max-lines */
